import useDisplayName from "@src/shared/context/displayname/useDisplayName";
import { IFilter } from "@src/shared/context/searchfilter/IFilter";
import useSearchResult from "@src/shared/context/searchresult/useSearchResult";
import getFormattedNumber from "@src/shared/utils/getFormattedNumber";
import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ReactSelect from "react-select";

import { ReactSelectOptions } from "../../../../../types";
import { ButtonOption, ButtonsContainer, SelectContainer } from "../ChoiceFilter.sc";
import { DefaultReactSelectStyle } from "../ReactSelectStyle";

interface IMultipleChoiceFilter<T> {
    values?: T[];
    options: ReactSelectOptions<T>[];
    onChange?: (value: T[] | null) => void;
    placeholder?: string;
    mobileVariant?: "buttons" | "select";
    isSearchable?: boolean;
    isClearable?: boolean;
    isDisabled?: boolean;
    selectAll?: boolean;
    filterKey?: keyof IFilter;
}

const MultipleChoiceFilter = <T extends any>({
    values,
    options,
    onChange,
    placeholder,
    mobileVariant = "select",
    isSearchable = false,
    isClearable = false,
    isDisabled = false,
    selectAll = false,
    filterKey,
}: IMultipleChoiceFilter<T>) => {
    const searchResult = useSearchResult();
    const intl = useIntl();
    const { getDisplayName } = useDisplayName();

    //This is because the lack of support of SSR in react-select
    const [currentValue, setCurrentValue] = React.useState<ReactSelectOptions<T>[]>([]);

    React.useEffect(() => {
        let defaultOptions: ReactSelectOptions<T>[] = [];
        const facetOptions = valuesForOptions(options, values);
        if (values && filterKey) {
            for (const value of values) {
                const facetLabel = facetOptions.find((facetOption) => (facetOption ? facetOption.value === value : ""))?.label;
                const displayName = getDisplayName(filterKey, String(value) ?? "");

                const label = facetLabel ? facetLabel : displayName ? `${displayName} (0)` : "";

                defaultOptions.push({ value, label });
            }
        } else {
            defaultOptions = facetOptions;
        }

        setCurrentValue(defaultOptions);
    }, [values, options, filterKey, getDisplayName]);

    const handleClick = (option: ReactSelectOptions<T>) => {
        if (currentValue.includes(option)) {
            handleSelectChange(currentValue.filter((value) => value !== option));
        } else {
            handleSelectChange([...currentValue, option]);
        }
    };

    const handleSelectChange = (options?: ReactSelectOptions<T>[]) => {
        if (onChange) {
            if (!options) {
                onChange(null);
            } else {
                const newValues = options?.map((item) => item.value);
                onChange(newValues);
            }
        }
    };

    return (
        <div>
            <ButtonsContainer isVisibleMobile={mobileVariant === "buttons"}>
                {selectAll && (
                    <ButtonOption selected={!values} onClick={() => handleSelectChange(undefined)}>
                        <FormattedMessage
                            id="leaseme.filter.all"
                            defaultMessage="Alle ({count})"
                            values={{
                                count: getFormattedNumber(searchResult.totalCount),
                            }}
                        />
                    </ButtonOption>
                )}
                {options.map((option) => (
                    <ButtonOption key={option.label} selected={currentValue.includes(option)} onClick={() => handleClick(option)}>
                        {option.label}
                    </ButtonOption>
                ))}
            </ButtonsContainer>
            <SelectContainer isVisibleMobile={mobileVariant === "select"}>
                <ReactSelect
                    value={currentValue}
                    styles={DefaultReactSelectStyle}
                    options={options}
                    onChange={handleSelectChange}
                    isMulti={true}
                    multiple
                    placeholder={placeholder}
                    isSearchable={isSearchable}
                    isClearable={isClearable}
                    isDisabled={isDisabled}
                    noOptionsMessage={() =>
                        intl.formatMessage({
                            id: "leasme.filter.noOption",
                            defaultMessage: "Keine Optionen",
                        })
                    }
                />
            </SelectContainer>
        </div>
    );
};

export default MultipleChoiceFilter;

function valuesForOptions<T>(options: ReactSelectOptions<T>[], values?: T[]) {
    return values?.map((value) => options.find((option) => option.value === value)!) ?? [];
}
